import { FaroBulkDialog } from "@components/common/faro-table/bulk-dialog/bulk-dialog";
import { BulkSuccessDialog } from "@components/common/faro-table/bulk-dialog/bulk-success-dialog";
import { AccessLevelMenuButton } from "@components/access-level/access-level-menu-button";
import { useChangeAccessLevelBulkDialogContent } from "@components/table/projects/bulk-actions/bulk-change-access-level-helper";
import { SdbProject } from "@custom-types/project-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { updateProjectDetails } from "@store/projects/projects-slice-thunk";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import {
  bulkActionNameSelector,
  selectedEntitiesSelector,
  shouldShowSuccessDialogSelector,
} from "@store/table/table-selector";
import { setBulkActionName } from "@store/table/table-slice";
import { BulkActionEvents } from "@utils/track-event/track-event-list";
import { useEffect, useState } from "react";

/** Button allowing to change project access level in company */
export function BulkChangeProjectAccessLevel(): JSX.Element {
  const [shouldShowDialog, setShouldShowDialog] = useState<boolean>(false);
  const [newAccessLevel, setNewAccessLevel] =
    useState<FaroCoreApiTypes.ProjectAccessLevel>("private");

  const bulkActionName = useAppSelector(bulkActionNameSelector);

  const dispatch = useAppDispatch();
  const shouldShowSuccessDialog = useAppSelector(
    shouldShowSuccessDialogSelector
  );
  const selectedEntities = useAppSelector(selectedEntitiesSelector("projects"));

  const { bulkDialogContent, successDialogContent } =
    useChangeAccessLevelBulkDialogContent("project", newAccessLevel);

  // Closes the bulk dialog when the bulk action is successful to show the success dialog
  useEffect(() => {
    if (shouldShowSuccessDialog) {
      setShouldShowDialog(false);
    }
  }, [shouldShowSuccessDialog]);

  function onAccessLevelChange(
    accessLevel: FaroCoreApiTypes.ProjectAccessLevel
  ): void {
    dispatch(setBulkActionName("changeAccessLevel"));
    setNewAccessLevel(accessLevel);
    setShouldShowDialog(true);
  }

  return (
    <>
      <AccessLevelMenuButton
        type="project"
        onAccessLevelChange={onAccessLevelChange}
      />

      {/* Checking for bulkActionName to make sure the dialog of another bulk action is not opened by mistake */}
      {bulkActionName === "changeAccessLevel" && shouldShowSuccessDialog && (
        <BulkSuccessDialog dialogContent={successDialogContent} />
      )}

      {bulkActionName === "changeAccessLevel" && shouldShowDialog && (
        <FaroBulkDialog
          dialogContent={bulkDialogContent}
          onClose={() => setShouldShowDialog(false)}
          trackingEvent={BulkActionEvents.bulkChangeGroup}
          bulkActionCallback={(project: SdbProject) =>
            dispatch(
              updateProjectDetails({
                projectId: project.id,
                payload: {
                  accessLevel: newAccessLevel,
                },
              })
            ).unwrap()
          }
          selectedEntities={selectedEntities}
          uniqueIdKey="id"
          tableDataType="projects"
        />
      )}
    </>
  );
}
